import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  DialogActions,
  TextField,
  Typography,
} from "@mui/material";
import ModalBasicLayout from "../ModalBasicLayout";
import ContainerModalActions from "../../Containers/ContainerModalActions";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp } from "../../../lib/redux";
import {
  BUTTON_CANCEL,
  BUTTON_DUPLICATE,
  BUTTON_GO_BACK,
  DUPLICATE_AD_OTHER_GROUP_AD_GROUP_LABEL,
  DUPLICATE_AD_OTHER_GROUP_AD_GROUP_PLACEHOLDER,
  DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_LABEL,
  DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_CHANGE_WARNING,
  DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_PLACEHOLDER,
  DUPLICATE_AD_OTHER_GROUP_EMPTY_AD_GROUPS,
  DUPLICATE_AD_OTHER_GROUP_MODAL_DESCRIPTION,
  DUPLICATE_AD_OTHER_GROUP_MODAL_TITLE,
  DUPLICATE_AD_GROUP_CAMPAIGN_CHANGE_WARNING,
  DUPLICATE_AD_GROUP_CAMPAIGN_TYPE_MISMATCH,
  DUPLICATE_CAMPAIGN_AD_FORMAT_MISMATCH,
  DUPLICATE_AD_GROUP_MODAL_DESCRIPTION,
  DUPLICATE_AD_GROUP_MODAL_TITLE,
  DUPLICATE_ASSETS_GROUP_CAMPAIGN_CHANGE_WARNING,
  DUPLICATE_ASSETS_GROUP_MODAL_DESCRIPTION,
  DUPLICATE_ASSETS_GROUP_MODAL_TITLE,
  WAIT,
} from "../../../i18n/keysTranslations";
import {
  startGetAdsCampaign,
  startGetAdsCampaigns,
} from "../../../actions/adsCampaigns";
import { DEFAULT_SORT_CONFIG, LINKEDIN } from "../../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import { ConfirmAlert } from "../../Alerts/Alerts";

export const DUPLICATE_MODAL_MODE_AD = "ad";
export const DUPLICATE_MODAL_MODE_AD_GROUP = "adGroup";
export const DUPLICATE_MODAL_MODE_ASSETS_GROUP = "assetsGroup";

const PROFILE_MISMATCH_OBJECTIVE = "objective";
const PROFILE_MISMATCH_AD_FORMAT = "adFormat";

const MODE_TEXTS = {
  [DUPLICATE_MODAL_MODE_AD]: {
    title: DUPLICATE_AD_OTHER_GROUP_MODAL_TITLE,
    description: DUPLICATE_AD_OTHER_GROUP_MODAL_DESCRIPTION,
    changeWarning: DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_CHANGE_WARNING,
  },
  [DUPLICATE_MODAL_MODE_AD_GROUP]: {
    title: DUPLICATE_AD_GROUP_MODAL_TITLE,
    description: DUPLICATE_AD_GROUP_MODAL_DESCRIPTION,
    changeWarning: DUPLICATE_AD_GROUP_CAMPAIGN_CHANGE_WARNING,
  },
  [DUPLICATE_MODAL_MODE_ASSETS_GROUP]: {
    title: DUPLICATE_ASSETS_GROUP_MODAL_TITLE,
    description: DUPLICATE_ASSETS_GROUP_MODAL_DESCRIPTION,
    changeWarning: DUPLICATE_ASSETS_GROUP_CAMPAIGN_CHANGE_WARNING,
  },
};

const SCROLL_BOTTOM_THRESHOLD = 20;
const CAMPAIGNS_PAGE_SIZE = 20;
const MIN_CAMPAIGN_OPTIONS_PER_LOAD = 20;
const MAX_PAGES_PER_LOAD = 10;
const AUTOCOMPLETE_PAPER_SX = {
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
};
const AUTOCOMPLETE_TEXT_FIELD_SX = {
  backgroundColor: "fields.background",
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
};

export const getCampaignProfile = ({ campaign, platform }) => {
  const platformData = campaign?.platformsData?.[platform];

  return {
    objective: platformData?.objective,
    adFormat: platform === LINKEDIN ? platformData?.adFormat : undefined,
  };
};

const hasCampaignProfile = (profile) =>
  !!profile?.objective || !!profile?.adFormat;

const getCampaignProfileMismatch = ({ sourceProfile, targetProfile }) => {
  if (
    sourceProfile?.objective &&
    sourceProfile.objective !== targetProfile?.objective
  ) {
    return PROFILE_MISMATCH_OBJECTIVE;
  }

  if (
    sourceProfile?.adFormat &&
    sourceProfile.adFormat !== targetProfile?.adFormat
  ) {
    return PROFILE_MISMATCH_AD_FORMAT;
  }

  return null;
};

const mapCampaignToOption = ({ campaignID, campaign, platform }) => {
  const campaignName =
    campaign?.platformsData?.[platform]?.name ||
    campaign?.platformsData?.[campaign?.platforms?.[0]]?.name ||
    campaign?.name ||
    campaignID;

  return {
    id: campaignID,
    name: campaignName,
    campaignProfile: getCampaignProfile({ campaign, platform }),
  };
};

const mapAdGroupsToOptions = ({ adGroups = [], platform }) => {
  return (adGroups || [])
    .filter((adGroup) => adGroup?.platform === platform)
    .map((adGroup) => ({
      id: adGroup.id,
      name: adGroup.name || adGroup.id,
    }));
};

const getMergedCampaignOptions = ({
  defaultCampaignOption,
  previousOptions,
  newOptions,
}) => {
  const optionsMap = new Map();

  [defaultCampaignOption, ...previousOptions, ...newOptions].forEach((item) => {
    if (item?.id) {
      optionsMap.set(item.id, item);
    }
  });

  return Array.from(optionsMap.values());
};

const ModalDuplicateAdToAdGroup = ({
  modalOpen,
  onCloseModal = () => {},
  currentCampaignID,
  currentCampaignName,
  platform,
  mode = DUPLICATE_MODAL_MODE_AD,
  sourceCampaignProfile,
  currentCampaignAdGroups = [],
  onConfirmDuplicate = async () => false,
}) => {
  const isCampaignOnlyMode = mode !== DUPLICATE_MODAL_MODE_AD;
  const modeTexts = MODE_TEXTS[mode] || MODE_TEXTS[DUPLICATE_MODAL_MODE_AD];
  const shouldValidateCampaignType =
    isCampaignOnlyMode && hasCampaignProfile(sourceCampaignProfile);

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const [campaignOptions, setCampaignOptions] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [adGroupOptions, setAdGroupOptions] = useState([]);
  const [selectedAdGroup, setSelectedAdGroup] = useState(null);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoadingAdGroups, setIsLoadingAdGroups] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasMoreCampaigns, setHasMoreCampaigns] = useState(true);
  const [lastCampaignsKey, setLastCampaignsKey] = useState(null);
  const [campaignDraftByID, setCampaignDraftByID] = useState({});
  const [selectedCampaignProfile, setSelectedCampaignProfile] = useState(null);
  const [isLoadingCampaignType, setIsLoadingCampaignType] = useState(false);

  const campaignProfileMismatch =
    shouldValidateCampaignType &&
    !isLoadingCampaignType &&
    !!selectedCampaign?.id
      ? getCampaignProfileMismatch({
          sourceProfile: sourceCampaignProfile,
          targetProfile: selectedCampaignProfile,
        })
      : null;
  const isCampaignTypeMismatch = !!campaignProfileMismatch;

  const isDuplicatingIntoAnotherCampaign =
    !!selectedCampaign?.id &&
    !!currentCampaignID &&
    selectedCampaign.id !== currentCampaignID;

  const defaultCampaignOption = useMemo(
    () => ({
      id: currentCampaignID,
      name: currentCampaignName || currentCampaignID,
    }),
    [currentCampaignID, currentCampaignName]
  );

  const onResetState = () => {
    setCampaignOptions([]);
    setSelectedCampaign(defaultCampaignOption);
    setAdGroupOptions([]);
    setSelectedAdGroup(null);
    setIsLoadingCampaigns(false);
    setIsLoadingAdGroups(false);
    setIsSubmitting(false);
    setHasMoreCampaigns(true);
    setLastCampaignsKey(null);
    setCampaignDraftByID({});
    setSelectedCampaignProfile(null);
    setIsLoadingCampaignType(false);
  };

  const onResolveCampaignType = async (campaignOption) => {
    if (!campaignOption?.id) {
      setSelectedCampaignProfile(null);
      return;
    }

    if (campaignOption.id === currentCampaignID) {
      setSelectedCampaignProfile(sourceCampaignProfile);
      return;
    }

    if (hasCampaignProfile(campaignOption.campaignProfile)) {
      setSelectedCampaignProfile(campaignOption.campaignProfile);
      return;
    }

    setIsLoadingCampaignType(true);
    setSelectedCampaignProfile(null);

    try {
      const campaign =
        campaignDraftByID[campaignOption.id] ||
        (await dispatch(startGetAdsCampaign(campaignOption.id, true)));

      if (campaign) {
        setCampaignDraftByID((prev) => ({
          ...prev,
          [campaignOption.id]: campaign,
        }));
      }

      setSelectedCampaignProfile(getCampaignProfile({ campaign, platform }));
    } finally {
      setIsLoadingCampaignType(false);
    }
  };

  const onLoadCampaigns = async () => {
    if (isLoadingCampaigns || !hasMoreCampaigns) return;
    setIsLoadingCampaigns(true);

    try {
      let cursor = lastCampaignsKey;
      let hasMore = true;
      let collectedCount = 0;
      let pagesFetched = 0;

      while (
        hasMore &&
        collectedCount < MIN_CAMPAIGN_OPTIONS_PER_LOAD &&
        pagesFetched < MAX_PAGES_PER_LOAD
      ) {
        let nextCursor = cursor;
        const response = await dispatch(
          startGetAdsCampaigns({
            lastKey: cursor,
            limit: CAMPAIGNS_PAGE_SIZE,
            sortConfig: DEFAULT_SORT_CONFIG,
            onChangeLastKey: (newKey) => {
              nextCursor = newKey;
              setLastCampaignsKey(newKey);
            },
            onLoadLastKey: () => {
              hasMore = false;
              setHasMoreCampaigns(false);
            },
          })
        );
        pagesFetched += 1;

        if (response && typeof response === "object") {
          const newOptions = Object.entries(response)
            .filter(([, campaign]) => campaign?.platforms?.includes(platform))
            .map(([campaignID, campaign]) =>
              mapCampaignToOption({ campaignID, campaign, platform })
            );

          setCampaignOptions((previousOptions) =>
            getMergedCampaignOptions({
              defaultCampaignOption,
              previousOptions,
              newOptions,
            })
          );
          collectedCount += newOptions.length;
        }

        if (!response || typeof response !== "object") break;
        if (nextCursor === cursor) break;
        cursor = nextCursor;
      }
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  const onLoadAdGroupsFromCampaign = async (campaignOption) => {
    if (!campaignOption?.id) {
      setAdGroupOptions([]);
      setSelectedAdGroup(null);
      return;
    }

    setIsLoadingAdGroups(true);
    setSelectedAdGroup(null);

    if (
      campaignOption.id === currentCampaignID &&
      Array.isArray(currentCampaignAdGroups)
    ) {
      const currentOptions = mapAdGroupsToOptions({
        adGroups: currentCampaignAdGroups,
        platform,
      });
      setAdGroupOptions(currentOptions);
      setIsLoadingAdGroups(false);
      return;
    }

    const campaignFromCache = campaignDraftByID[campaignOption.id];
    const campaign =
      campaignFromCache ||
      (await dispatch(startGetAdsCampaign(campaignOption.id, true)));

    if (!campaign) {
      setAdGroupOptions([]);
      setIsLoadingAdGroups(false);
      return;
    }

    setCampaignDraftByID((prev) => ({
      ...prev,
      [campaignOption.id]: campaign,
    }));

    const options = mapAdGroupsToOptions({
      adGroups: campaign?.adsGroups || [],
      platform,
    });

    setAdGroupOptions(options);
    setIsLoadingAdGroups(false);
  };

  const onClose = () => {
    onCloseModal();
  };

  const onSubmit = async () => {
    if (disableSubmit) return;

    if (isDuplicatingIntoAnotherCampaign) {
      const result = await ConfirmAlert({
        title: t(WAIT),
        text: t(modeTexts.changeWarning),
        confirmButtonText: t(BUTTON_DUPLICATE),
        cancelButtonText: t(BUTTON_GO_BACK),
      });
      if (!result.isConfirmed) {
        onClose();
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const ok = await onConfirmDuplicate({
        targetCampaignID: selectedCampaign.id,
        targetAdGroupID: selectedAdGroup?.id,
      });

      if (ok) {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCampaignsListScroll = (event) => {
    const listboxNode = event.currentTarget;
    const isBottomReached =
      listboxNode.scrollTop + listboxNode.clientHeight >=
      listboxNode.scrollHeight - SCROLL_BOTTOM_THRESHOLD;

    if (isBottomReached && hasMoreCampaigns && !isLoadingCampaigns) {
      onLoadCampaigns();
    }
  };

  useEffect(() => {
    if (!modalOpen) return;
    onResetState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;
    const onLoadInitial = async () => {
      setCampaignOptions([defaultCampaignOption]);
      setSelectedCampaign(defaultCampaignOption);
      await onLoadCampaigns();
    };

    onLoadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, defaultCampaignOption.id, platform]);

  useEffect(() => {
    if (!modalOpen || isCampaignOnlyMode) return;
    onLoadAdGroupsFromCampaign(selectedCampaign);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaign, modalOpen]);

  useEffect(() => {
    if (!modalOpen || !shouldValidateCampaignType) return;
    onResolveCampaignType(selectedCampaign);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaign, modalOpen, shouldValidateCampaignType]);

  const disableSubmit =
    isSubmitting ||
    !selectedCampaign?.id ||
    isLoadingCampaignType ||
    isCampaignTypeMismatch ||
    (!isCampaignOnlyMode && (isLoadingAdGroups || !selectedAdGroup?.id));

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onClose}
      title={t(modeTexts.title)}
      description={t(modeTexts.description)}
      maxWidth="sm"
      fullWidth
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pt: 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body1" color="text.primary">
            {t(DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_LABEL)}
          </Typography>
          <Autocomplete
            options={campaignOptions}
            value={selectedCampaign}
            loading={isLoadingCampaigns}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => setSelectedCampaign(value)}
            ListboxProps={{
              onScroll: onCampaignsListScroll,
            }}
            PaperProps={{
              sx: AUTOCOMPLETE_PAPER_SX,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                hiddenLabel={true}
                placeholder={t(DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_PLACEHOLDER)}
                variant="filled"
                sx={AUTOCOMPLETE_TEXT_FIELD_SX}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingCampaigns ? (
                        <CircularProgress size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        {isCampaignTypeMismatch && (
          <Typography variant="caption" color="error.main">
            {t(
              campaignProfileMismatch === PROFILE_MISMATCH_AD_FORMAT
                ? DUPLICATE_CAMPAIGN_AD_FORMAT_MISMATCH
                : DUPLICATE_AD_GROUP_CAMPAIGN_TYPE_MISMATCH
            )}
          </Typography>
        )}

        {!isCampaignOnlyMode && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="body1" color="text.primary">
              {t(DUPLICATE_AD_OTHER_GROUP_AD_GROUP_LABEL)}
            </Typography>
            <Autocomplete
              options={adGroupOptions}
              value={selectedAdGroup}
              loading={isLoadingAdGroups}
              disabled={!selectedCampaign?.id}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => setSelectedAdGroup(value)}
              noOptionsText={t(DUPLICATE_AD_OTHER_GROUP_EMPTY_AD_GROUPS)}
              PaperProps={{
                sx: AUTOCOMPLETE_PAPER_SX,
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  hiddenLabel={true}
                  placeholder={t(DUPLICATE_AD_OTHER_GROUP_AD_GROUP_PLACEHOLDER)}
                  variant="filled"
                  sx={AUTOCOMPLETE_TEXT_FIELD_SX}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingAdGroups ? (
                          <CircularProgress size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}

        {!selectedCampaign?.id && (
          <Typography variant="caption" color="error.main">
            {t(DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_PLACEHOLDER)}
          </Typography>
        )}
      </Box>

      <DialogActions>
        <ContainerModalActions
          propsPrimaryButton={{
            showButton: true,
            onClick: onSubmit,
            text: BUTTON_DUPLICATE,
            disabled: disableSubmit,
            loading: isSubmitting,
          }}
          propsSecondaryButton={{
            showButton: true,
            onClick: onClose,
            text: BUTTON_CANCEL,
            disabled: isSubmitting,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalDuplicateAdToAdGroup;
